import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';
import { useVendorPlan } from '../../../common/hooks/use-vendor-plan.hook';
import { PlanLimitKey, UsagePair, isUnlimited, meterState } from './plan-limits';
import { UnlockCallout } from './UnlockCallout';
import { UsageMeter } from './UsageMeter';
import { usePlanUsage } from './usePlanUsage';

/**
 * A page's monthly limits, as meters, in one card — and, once one of them
 * passes 80%, a line under them on what Pro changes.
 *
 * Draws nothing when the plan meters none of the keys (every paid plan, and
 * every API older than the free plan), so it can sit on a page for everyone.
 */
export function PlanMeters({
  keys,
  title,
  className,
  testId = 'plan-meters',
}: {
  keys: PlanLimitKey[];
  title?: string;
  className?: string;
  testId?: string;
}) {
  const { t } = useTranslation();
  const { isFree } = useVendorPlan();
  const { meter } = usePlanUsage();

  const items = keys
    .map((key) => ({ key, pair: meter(key) }))
    .filter((m): m is { key: PlanLimitKey; pair: UsagePair } => !!m.pair && !isUnlimited(m.pair.limit));
  if (!items.length) return null;

  const at = items.find((m) => meterState(m.pair.current, m.pair.limit) === 'at');
  const near = items.find((m) => meterState(m.pair.current, m.pair.limit) === 'near');
  const pressing = at ?? near;

  return (
    <section data-testid={testId} className={cn('rounded-xl border bg-card p-4 sm:p-5', className)}>
      <p className="text-sm font-medium">{title ?? t(isFree ? 'FREE_PLAN.METERS.TITLE' : 'FREE_PLAN.METERS.TITLE_PLAN')}</p>
      <div className={cn('mt-3 grid gap-4', items.length > 1 && 'sm:grid-cols-2', items.length > 2 && 'lg:grid-cols-3')}>
        {items.map(({ key, pair }) => (
          <UsageMeter key={key} limitKey={key} current={pair.current} limit={pair.limit} />
        ))}
      </div>
      {pressing && (
        <UnlockCallout
          testId={`${testId}-callout`}
          className="mt-4 border-dashed bg-transparent p-3"
          reason={pressing.key}
          title={t(at ? 'FREE_PLAN.METERS.AT' : 'FREE_PLAN.METERS.NEAR', { what: t(`FREE_PLAN.WHAT.${pressing.key}`) })}
          body={t(`FREE_PLAN.PRO_GETS.${pressing.key}`, { defaultValue: t('FREE_PLAN.PRO_GETS.GENERIC') })}
        />
      )}
    </section>
  );
}
